import { ENDPOINT_NAMES } from '../../../constants';
import type { FieldingGroupRead, StepBase } from '../../../rtk/rtk.generated';
import type { ProjectApi } from '../../../rtk/types';
import type { AppDispatch, RootState } from '../../types';
import { findMatchingCachedQueries, OptimisticUpdater } from '../../utils';

interface PatchStepArgs {
  fieldingGroupId: string;
  stepId: string;
  stepBase: StepBase;
}

interface PatchStepContext {
  dispatch: AppDispatch;
  queryFulfilled: Promise<{ data: StepBase }>;
  getState: () => RootState;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type FieldingGroupQueryArgs = {
  projectId: number;
  highlighterId?: string;
  expand?: boolean;
};

/**
 * ### findProjectIdFromCachedQueries
 *
 * @description
 * Searches through cached fielding group queries to find the projectId
 * for a given fieldingGroupId.
 *
 * @param queries - The queries object from RTK Query state
 * @param fieldingGroupId - The fielding group ID to search for
 * @returns The projectId if found, undefined otherwise
 */
const findProjectIdFromCachedQueries = (
  queries: Record<string, unknown> | undefined,
  fieldingGroupId: string,
): number | undefined => {
  if (!queries) return undefined;

  // Search through all queries to find fielding group queries
  for (const [, query] of Object.entries(queries)) {
    const cachedQuery = query as {
      endpointName?: string;
      originalArgs?: FieldingGroupQueryArgs;
      data?: FieldingGroupRead[];
    };

    if (
      cachedQuery?.endpointName === ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP &&
      cachedQuery?.data &&
      Array.isArray(cachedQuery.data)
    ) {
      // Check if this query contains the fielding group we're looking for
      const fieldingGroup = cachedQuery.data.find((fg) => fg.id === fieldingGroupId);
      if (fieldingGroup) {
        // Return projectId from query args or from the fielding group itself
        return cachedQuery.originalArgs?.projectId ?? fieldingGroup.projectId;
      }
    }
  }

  return undefined;
};

/**
 * ### updateStepInFieldingGroupCache
 *
 * @description
 * Updates a single step within a specific fielding group in a draft array.
 *
 * @param draft - The draft array containing fielding groups
 * @param fieldingGroupId - The ID of the fielding group containing the step
 * @param stepId - The ID of the step to update
 * @param step - The updated step data
 */
const updateStepInFieldingGroupCache = (
  draft: FieldingGroupRead[] | undefined,
  fieldingGroupId: string,
  stepId: string,
  step: StepBase,
): void => {
  if (!draft) return;

  const fieldingGroup = draft.find((fg) => fg.id === fieldingGroupId);
  if (fieldingGroup?.steps) {
    const stepIndex = fieldingGroup.steps.findIndex((s) => s.id === stepId);
    if (stepIndex >= 0) {
      fieldingGroup.steps[stepIndex] = step;
    }
  }
};

/**
 * ### Patch Step Enhancer
 *
 * @description
 * Enhancer for patchFieldingGroupsByFieldingGroupIdStepsAndStepId endpoint.
 * Optimistically updates the step in the cache before the server responds.
 * Updates both the direct steps query and the steps array within fielding groups.
 * If the request fails, the update is rolled back.
 *
 * @param api - The ProjectApi instance
 * @returns Endpoint enhancer configuration
 */
const patchFieldingGroupsByFieldingGroupIdStepsAndStepId = (api: ProjectApi) => ({
  // Disable tag invalidation since we're using optimistic updates
  // The generated RTK code has invalidatesTags: ['Steps', 'FieldingGroup'] which would
  // cause the GET queries to refetch and override our optimistic update
  invalidatesTags: () => [],
  async onQueryStarted(args: PatchStepArgs, context: PatchStepContext) {
    const { fieldingGroupId, stepId, stepBase } = args;
    const { dispatch, queryFulfilled, getState } = context;

    const updater = new OptimisticUpdater({ dispatch, api });

    // Update the direct steps query cache
    updater.update({
      endpointName: ENDPOINT_NAMES.GET_FIELDING_GROUPS_BY_FIELDING_GROUP_ID_STEPS,
      args: { fieldingGroupId },
      updateFn: (draft: StepBase[]) => {
        const stepIndex = draft.findIndex((s) => s.id === stepId);
        if (stepIndex >= 0) {
          draft[stepIndex] = stepBase;
        }
      },
    });

    // Find projectId by searching cached fielding group queries
    const state = getState();
    const queries = state.projectApi?.queries;
    const projectId = findProjectIdFromCachedQueries(queries, fieldingGroupId);

    if (projectId) {
      // Find all matching cached fielding group queries for this project
      // This handles queries with different highlighterId/expand parameters
      const matchingQueries = findMatchingCachedQueries<FieldingGroupQueryArgs>({
        queries,
        endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
        matcher: (queryArgs) => queryArgs.projectId === projectId,
      });

      // Update all matching cached queries (handles variations like different expand/highlighterId values)
      matchingQueries.forEach((queryArgs) => {
        updater.update({
          endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
          args: queryArgs,
          updateFn: (draft: FieldingGroupRead[]) =>
            updateStepInFieldingGroupCache(draft, fieldingGroupId, stepId, stepBase),
        });
      });

      // Also update the most common query pattern directly
      updater.update({
        endpointName: ENDPOINT_NAMES.GET_PROJECTS_BY_PROJECT_ID_FIELDING_GROUP,
        args: { projectId, expand: true },
        updateFn: (draft: FieldingGroupRead[]) =>
          updateStepInFieldingGroupCache(draft, fieldingGroupId, stepId, stepBase),
      });
    }

    try {
      await queryFulfilled;
    } catch {
      // Rollback all optimistic updates on failure
      updater.rollback();
    }
  },
});

export default patchFieldingGroupsByFieldingGroupIdStepsAndStepId;
